/**
 * Camada de acesso a dados (DEMO). Todas as funções são assíncronas para
 * espelhar o contrato de uma API real (Supabase). Validações de autorização
 * aqui são ilustrativas: na versão real elas DEVEM ocorrer no servidor.
 */
import { store } from './storage';
import { uid, normalize } from '@/lib/format';
import { precheckContent } from '@/lib/moderation';
import { supabase } from '@/lib/supabase';

const delay = (ms = 260) => new Promise((r) => setTimeout(r, ms));
const now = () => new Date().toISOString();

async function hash(text) {
  try {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`blb-demo:${text}`));
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    let h = 0;
    for (let i = 0; i < text.length; i += 1) h = (h * 33 + text.charCodeAt(i)) >>> 0;
    return `fallback-${h}`;
  }
}

const currentUser = () => {
  const s = store.getState();
  return s.session ? s.users.find((u) => u.id === s.session.userId) || null : null;
};

/* ---------------- Empresas (Supabase) ---------------- */
const PUBLIC_COLS = 'id,name,tagline,description,category_id,extra_category_ids,business_type,uf,city,services,products,portfolio,website,socials,verified,plan,sponsored,featured,status,created_at,updated_at,logo_url';
const OWNER_COLS = `${PUBLIC_COLS},owner_id,whatsapp,email,cnpj`;

const FIELD_MAP = {
  name: 'name',
  logoUrl: 'logo_url',
  tagline: 'tagline',
  description: 'description',
  categoryId: 'category_id',
  extraCategoryIds: 'extra_category_ids',
  businessType: 'business_type',
  services: 'services',
  products: 'products',
  portfolio: 'portfolio',
  website: 'website',
  whatsapp: 'whatsapp',
  email: 'email',
  socials: 'socials',
  cnpj: 'cnpj',
};

function toRow(data) {
  const row = {};
  for (const [key, column] of Object.entries(FIELD_MAP)) {
    if (key in data) row[column] = data[key];
  }
  if (data.location) {
    row.uf = data.location.uf;
    row.city = data.location.city;
  }
  return row;
}

export function toCompany(row) {
  if (!row) return null;
  return {
    id: row.id,
    name: row.name,
    tagline: row.tagline,
    description: row.description,
    categoryId: row.category_id,
    extraCategoryIds: row.extra_category_ids || [],
    businessType: row.business_type,
    location: { uf: row.uf, city: row.city },
    services: row.services || [],
    products: row.products || [],
    portfolio: row.portfolio || [],
    website: row.website ?? null,
    whatsapp: row.whatsapp ?? null,
    email: row.email ?? null,
    socials: row.socials || {},
    cnpj: row.cnpj ?? null,
    verified: row.verified,
    plan: row.plan,
    sponsored: row.sponsored,
    featured: row.featured,
    status: row.status,
    ownerId: row.owner_id ?? null,
    cnpjStatus: row.cnpj_status ?? 'pendente',
    logoUrl: row.logo_url ?? null,
    isDemo: false,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

async function requireUserId() {
  const { data } = await supabase.auth.getUser();
  if (!data?.user) throw new Error('Faça login para continuar.');
  return data.user.id;
}

export const companyService = {
    async get(id) {
    const { data, error } = await supabase.from('companies').select(PUBLIC_COLS).eq('id', id).maybeSingle();
    if (error) throw new Error(error.message);
    return toCompany(data);
  },

  /** WhatsApp, e-mail e CNPJ: só membros com empresa ativa (ou admin). Retorna null se não puder ver. */
  async getContacts(id) {
    const { data, error } = await supabase.rpc('get_company_contacts', { p_company_id: id });
    if (error) throw new Error(error.message);
    return (Array.isArray(data) ? data[0] : data) || null;
  },

  /** Empresa vinculada ao usuário logado (ou null se ainda não tiver). */
  async getMine() {
    await requireUserId();
    const { data, error } = await supabase.rpc('get_my_company');
    if (error) throw new Error(error.message);
    return toCompany(Array.isArray(data) ? data[0] : data);
  },

  async create(data) {
    const ownerId = await requireUserId();
    const { error } = await supabase
      .from('companies')
      .insert({ ...toRow(data), owner_id: ownerId });
    if (error) throw new Error(error.message);
    return this.getMine();
  },

  async update(id, patch) {
    const { error } = await supabase
      .from('companies')
      .update(toRow(patch))
      .eq('id', id);
    if (error) throw new Error(error.message);
    return this.getMine();
  },
  async moderate() {
    throw new Error('A moderação de empresas será liberada no painel admin.');
  },

  async remove(id) {
    const { error } = await supabase.from('companies').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },
};

/* ---------------- Oportunidades (Supabase) ---------------- */
const OPP_COLS =
  'id,company_id,title,type,category_id,summary,description,requirements,uf,city,target_ufs,published_at,deadline,contact_method,status,sponsored,moderation_flags,created_at,updated_at';

const OPP_FIELD_MAP = {
  title: 'title',
  type: 'type',
  categoryId: 'category_id',
  summary: 'summary',
  description: 'description',
  requirements: 'requirements',
  targetUfs: 'target_ufs',
  deadline: 'deadline',
  contactMethod: 'contact_method',
};

function toOppRow(data) {
  const row = {};
  for (const [key, column] of Object.entries(OPP_FIELD_MAP)) {
    if (key in data) row[column] = data[key];
  }
  if (data.location) {
    row.uf = data.location.uf;
    row.city = data.location.city;
  }
  return row;
}

export function toOpportunity(row) {
  if (!row) return null;
  return {
    id: row.id,
    title: row.title,
    type: row.type,
    categoryId: row.category_id,
    summary: row.summary,
    description: row.description,
    requirements: row.requirements || [],
    companyId: row.company_id,
    location: { uf: row.uf, city: row.city },
    targetUfs: row.target_ufs || [],
    publishedAt: row.published_at,
    deadline: row.deadline ?? null,
    contactMethod: row.contact_method,
    status: row.status,
    sponsored: !!row.sponsored,
    moderationFlags: row.moderation_flags || [],
    isDemo: false,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const opportunityService = {
  async create(data) {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user) throw new Error('Faça login para publicar.');

    let companyId = data.companyId;
    if (!companyId) {
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('id')
        .eq('owner_id', auth.user.id)
        .maybeSingle();
      if (companyError || !company) throw new Error('Cadastre sua empresa antes de publicar.');
      companyId = company.id;
    }

    const { data: row, error } = await supabase
      .from('opportunities')
      .insert({ ...toOppRow(data), company_id: companyId })
      .select(OPP_COLS)
      .single();
    if (error) throw new Error(error.message);
    return toOpportunity(row);
  },

  async update(id, patch) {
    if (patch.status && patch.status !== 'ativa') {
      throw new Error('Encerrar ou pausar publicações será liberado em breve.');
    }
    const { data: row, error } = await supabase
      .from('opportunities')
      .update(toOppRow(patch))
      .eq('id', id)
      .select(OPP_COLS)
      .single();
    if (error) throw new Error(error.message);
    return toOpportunity(row);
  },

  async moderate() {
    throw new Error('A moderação de publicações será liberada no painel admin.');
  },

  async remove(id) {
    const { error } = await supabase.from('opportunities').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },
};

/* ---------------- Denúncias (Supabase) ---------------- */
export const reportService = {
  async create({ targetType, targetId, reason, details }) {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user) throw new Error('Faça login para denunciar.');
    const { error } = await supabase.from('reports').insert({
      reporter_user_id: auth.user.id,
      target_type: targetType,
      target_id: String(targetId),
      reason,
      details: details?.trim() || null,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  },

  /** Fila de denúncias. Só o administrador consegue ler. */
  async list() {
    const { data, error } = await supabase
      .from('reports')
      .select('id, target_type, target_id, reason, details, status, created_at, resolved_at')
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return data || [];
  },

  async resolve(id, status) {
    const { error } = await supabase
      .from('reports')
      .update({ status, resolved_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },
};

/* ---------------- Contato (demo: nada é enviado) ---------------- */
export const contactService = {
  async send(payload) {
    await delay(400);
    const msg = { id: uid('msg'), ...payload, createdAt: now() };
    store.update((s) => ({ contacts: [msg, ...(s.contacts || [])] }));
    return msg;
  },
};

/* ---------------- Categorias ---------------- */
export const categoryService = {
  async add(name) {
    await delay(120);
    const id = normalize(name).replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || uid('cat');
    if (store.getState().categories.some((c) => c.id === id)) throw new Error('Já existe uma categoria com esse nome.');
    store.update((s) => ({ categories: [...s.categories, { id, name, icon: 'Shapes', active: true }] }));
  },
  async update(id, patch) {
    await delay(120);
    store.update((s) => ({ categories: s.categories.map((c) => (c.id === id ? { ...c, ...patch } : c)) }));
  },
};

/* ---------------- Autenticação (Supabase) ---------------- */
let cachedUser = null;

const toUser = (profile, email) => ({
  id: profile.id,
  name: profile.name,
  email,
  role: profile.role === 'admin' ? 'admin' : 'empresario',
  status: profile.status === 'suspended' ? 'suspensa' : 'ativa',
  companyId: null,
});

async function loadCurrentUser(session) {
  if (!session?.user) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('id, name, role, status')
    .eq('id', session.user.id)
    .single();
  if (error || !data) return null;
  return toUser(data, session.user.email);
}

// Mantém o usuário atual em memória para o código que lê a sessão de forma síncrona
supabase.auth.getSession().then(async ({ data }) => {
  cachedUser = await loadCurrentUser(data.session);
});
supabase.auth.onAuthStateChange(async (_event, session) => {
  cachedUser = await loadCurrentUser(session);
});

function currentUserSync() {
  return cachedUser;
}

const friendly = (msg = '') => {
  if (/Invalid login credentials/i.test(msg)) return 'E-mail ou senha incorretos.';
  if (/Email not confirmed/i.test(msg)) return 'Confirme seu e-mail antes de entrar.';
  if (/already registered|already been registered/i.test(msg)) return 'Já existe uma conta com este e-mail.';
  if (/Password should be at least/i.test(msg)) return 'A senha precisa ter pelo menos 6 caracteres.';
  return 'Não foi possível concluir. Tente novamente.';
};

export const authService = {
  currentUser: currentUserSync,

  async signup({ name, email, password }) {
    const { data, error } = await supabase.auth.signUp({
      email: normalize(email),
      password,
      options: { data: { name } },
    });
    if (error) throw new Error(friendly(error.message));
    if (!data.session) throw new Error('Cadastro criado. Confirme seu e-mail antes de entrar.');
    cachedUser = await loadCurrentUser(data.session);
    return cachedUser;
  },

  async login({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalize(email),
      password,
    });
    if (error) throw new Error(friendly(error.message));
    const user = await loadCurrentUser(data.session);
    if (!user) throw new Error('Perfil não encontrado. Tente novamente em instantes.');
    if (user.status === 'suspensa') {
      await supabase.auth.signOut();
      cachedUser = null;
      throw new Error('Esta conta está suspensa. Entre em contato com a administração.');
    }
    cachedUser = user;
    return user;
  },

  async loginAsSample() {
    throw new Error('Conta de exemplo desativada. Crie sua conta para começar.');
  },

  async logout() {
    await supabase.auth.signOut();
    cachedUser = null;
  },

  async recover(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(normalize(email), {
      redirectTo: `${window.location.origin}/`,
    });
    if (error) throw new Error(friendly(error.message));
    return { ok: true, email };
  },

  async updateAccount(patch) {
    const user = cachedUser;
    if (!user) throw new Error('Faça login para alterar sua conta.');
    if (patch.name) {
      const { error } = await supabase.from('profiles').update({ name: patch.name }).eq('id', user.id);
      if (error) throw new Error('Não foi possível salvar o nome.');
    }
    if (patch.email) {
      const { error } = await supabase.auth.updateUser({ email: normalize(patch.email) });
      if (error) throw new Error(friendly(error.message));
    }
    cachedUser = {
      ...user,
      name: patch.name ?? user.name,
      email: patch.email ? normalize(patch.email) : user.email,
    };
  },

  async changePassword(current, nextPassword) {
    const user = cachedUser;
    if (!user) throw new Error('Faça login para alterar sua senha.');
    const { error: checkError } = await supabase.auth.signInWithPassword({ email: user.email, password: current });
    if (checkError) throw new Error('Senha atual incorreta.');
    const { error } = await supabase.auth.updateUser({ password: nextPassword });
    if (error) throw new Error(friendly(error.message));
  },

  async moderateUser(id, patch) {
    const dbPatch = {};
    if (patch.status) dbPatch.status = patch.status === 'suspensa' ? 'suspended' : 'active';
    const { error } = await supabase.from('profiles').update(dbPatch).eq('id', id);
    if (error) throw new Error('Ação não permitida. Ela será liberada na etapa do painel admin.');
  },

    async deleteAccount() {
    const { data, error } = await supabase.functions.invoke('delete-my-account', { body: {} });
    if (error) {
      let msg = 'Não foi possível excluir a conta.';
      try {
        const j = await error.context.json();
        if (j?.error) msg = j.error;
      } catch (e) {
        // sem corpo de erro
      }
      throw new Error(msg);
    }
    if (data?.error) throw new Error(data.error);
    await supabase.auth.signOut();
    cachedUser = null;
  },
};

export const demoService = {
  reset: async () => {
    await delay(200);
    store.reset();
  },
};
